import React, { useState, useId } from 'react'
import { apiGet } from '~/util/api'
import useTranslation, { TranslationProvider } from './TranslationProvider'
import translations from './locales.json'

const AylaRatingFeedbackContent = ({
  routes,
  content,
  title,
  subtitle,
  className = '',
  documentType = '',
  starDescriptions = [],
  submittingText,
  submittedText,
  feedbackRating,
}) => {
  const { t } = useTranslation()
  const resolvedSubmittingText = submittingText || t('submitting')
  const resolvedSubmittedText = submittedText || t('thanks_for_feedback')
  const [hoveredRating, setHoveredRating] = useState(0)
  const [selectedRating, setSelectedRating] = useState(feedbackRating || 0)
  const [isSubmittingRating, setIsSubmittingRating] = useState(false)
  const [ratingSubmitted, setRatingSubmitted] = useState(false)

  const uid = useId()
  const createFeedbackRoute = routes?.createFeedback
  const hasPreviousRating = !!feedbackRating
  const handleStarClick = (star) => {
    if (
      isSubmittingRating ||
      ratingSubmitted ||
      hasPreviousRating ||
      !createFeedbackRoute ||
      !content
    ) {
      return
    }

    setSelectedRating(star)
    ;(async () => {
      try {
        setIsSubmittingRating(true)
        const query = new URLSearchParams({
          rating: String(star),
          content: String(content),
          feedback_type: documentType,
        })

        await apiGet(`${createFeedbackRoute}?${query.toString()}`)
        setRatingSubmitted(true)
      } catch (ratingError) {
        console.error('Error submitting rating:', ratingError)
      } finally {
        setIsSubmittingRating(false)
      }
    })()
  }

  return (
    <div
      className={`relative mx-auto mb-8 flex w-full flex-col items-center gap-3 overflow-hidden rounded-xl bg-white px-3 py-8 shadow-md sm:px-4 sm:py-12 ${className}`}
    >
      <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#75B21D] via-[#08467C] to-[#1C6F29]" />
      <h3
        id={`${uid}-title`}
        className="!font-roboto text-lg !font-bold text-intoo-text"
      >
        {title}
      </h3>
      <p className="text-sm font-medium text-intoo-text">{subtitle}</p>
      {hasPreviousRating && (
        <p className="sr-only">
          You previously rated this {selectedRating} out of 5 stars. Rating is
          read-only.
        </p>
      )}

      <div
        role="group"
        aria-labelledby={`${uid}-title`}
        className="grid grid-cols-5 gap-x-2 gap-y-2 text-center sm:gap-x-8"
        onMouseLeave={
          hasPreviousRating || ratingSubmitted
            ? undefined
            : () => setHoveredRating(0)
        }
      >
        {[1, 2, 3, 4, 5].map((star, index) => {
          const isFilled = (hoveredRating || selectedRating) >= star
          const description = starDescriptions[index] || ''

          return (
            <div key={star} className="flex flex-col items-center gap-2">
              <button
                type="button"
                aria-label={`${star} star${star > 1 ? 's' : ''}${description ? `: ${description}` : ''}`}
                aria-pressed={selectedRating === star}
                onMouseEnter={
                  hasPreviousRating || ratingSubmitted
                    ? undefined
                    : () => setHoveredRating(star)
                }
                onClick={() => handleStarClick(star)}
                disabled={
                  isSubmittingRating || ratingSubmitted || hasPreviousRating
                }
                className="rounded-sm leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08467C] focus-visible:ring-offset-2"
              >
                <svg
                  className={`h-8 w-8 transition-colors duration-150 sm:h-12 sm:w-12 ${isFilled ? 'text-amber-600' : 'text-gray-500'} ${hasPreviousRating || ratingSubmitted ? '' : 'cursor-pointer'}`}
                  fill={isFilled ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <polygon points="12 17.27 18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21 12 17.27" />
                </svg>
              </button>
              <span
                className={`max-w-[60px] text-xs leading-tight sm:max-w-[110px] sm:text-sm ${
                  isFilled ? 'text-intoo-text' : 'text-mine-shaft-600'
                }`}
              >
                {description}
              </span>
            </div>
          )
        })}
      </div>

      <div aria-live="polite" aria-atomic="true">
        {isSubmittingRating && (
          <p className="text-sm text-mine-shaft-600">
            {resolvedSubmittingText}
          </p>
        )}
        {ratingSubmitted && (
          <p className="text-sm text-mine-shaft-600">{resolvedSubmittedText}</p>
        )}
      </div>
    </div>
  )
}

const AylaRatingFeedback = (props) => {
  const { locale } = useTranslation()
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.shared.ayla_rating"
    >
      <AylaRatingFeedbackContent {...props} />
    </TranslationProvider>
  )
}

export default AylaRatingFeedback
